import React from 'react';
import { TextInput, View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { translate } from '../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type MyEmailInputProps = {
    label: string;
    labelFontSize?: number;
    required?: boolean;
    disabled?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    placeholder?: string;
} & ({
    value: string;
    onChangeText: (value: string) => void;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
} | {
    value?: never;
    onChangeText?: never;
    isInvalid?: never;
    setIsInvalid?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function MyEmailInput({
    label,
    value,
    onChangeText,
    labelFontSize,
    required = false,
    disabled = false,
    isInvalid,
    setIsInvalid,
    viewStyle,
    form,
    fieldName,
    customValidations,
    pattern,
    placeholder,
    labelStyle
}: MyEmailInputProps) {
    const { styles, theme } = useStyles(styleSheet);

    function isEmailValid(email: string) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }

    if (form && fieldName) {

        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        rules.validate = {
            validateEmail: (value) => isEmailValid(value) || translate('invalidEmail')
        };

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[{ rowGap: 5 }, viewStyle]}>
                    <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                        <MyAppText style={labelStyle || styles.label(labelFontSize || 14)}>{label}</MyAppText>
                        {required ?
                            <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInput
                        onBlur={field.onBlur}
                        value={field.value}
                        onChangeText={field.onChange}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        placeholder={placeholder || translate('email')}
                        style={[styles.input, {
                            color: !field.value ? theme.colors.placeholder : theme.colors.textColor
                        }, disabled ? styles.disabled : (isInvalid || fieldState.error ? styles.invalid : null)]}
                    />
                    {isInvalid ?
                        <MyAppText style={styles.error}>{translate('invalidEmail')}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;

    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[{ rowGap: 5 }, viewStyle]}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={labelStyle || styles.label(labelFontSize || 14)}>{label}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
                <TextInput
                    value={value}
                    onChangeText={onChangeText}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder={placeholder || translate('email')}
                    style={[styles.input, {
                        color: value == '' ? theme.colors.placeholder : theme.colors.textColor
                    }, disabled ? styles.disabled : (isInvalid ? styles.invalid : null)]}
                    onBlur={() => {
                        if (!value && !required) {
                            return setIsInvalid(false);
                        }
                        if (!value && required) {
                            return setIsInvalid(true);
                        }

                        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!regex.test(value)) {
                            return setIsInvalid(true);
                        }
                        return setIsInvalid(false);

                    }}
                />
                {isInvalid ?
                    <MyAppText style={styles.error}>{translate('invalidEmail')}</MyAppText> : null}
            </View>
        );
    }
}

const styleSheet = createStyleSheet((theme) => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        maxHeight: 40,
        padding: 10,
        borderRadius: 4,
        color: theme.colors.field.text,
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
    invalid: {
        borderColor: theme.colors.errors['500'],
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    }
}));
